import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { CoreModule } from '@core/core.module';
import { SigningElementPackageViewModelComplete } from '@models/e-signing';
import { STATUSES } from '@models/statuses';
import { EnvelopeService } from '@services/envelope/envelope.service';
import { UrlService } from '@services/url/url.service';
import { AvatarComponent } from '@ui/avatar/avatar.component';
import { IconsModule } from '@ui/icons/icons.module';
import { UiModule } from '@ui/ui.module';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { firstValueFrom } from 'rxjs';
import { PdfModalComponent } from '@components/pdf-modal/pdf-modal.component';
import { SignItApiService } from 'src/app/shared/api-repos/signit/signit.api.service';
import { Logger } from '@core/logger';

@Component({
	selector: 'app-e-signing-envelope-details',
	imports: [
		UiModule,
		CoreModule,
		CommonModule,
		AvatarComponent,
		RouterModule,
		IconsModule,
	],
	templateUrl: './e-signing-envelope-details.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ESigningEnvelopeDetailsComponent {
	private envelopeService = inject(EnvelopeService);
	private drawerService = inject(DrawerService);
	private signItApiService = inject(SignItApiService);
	public documentNames = input.required<string[]>();
	public envelope = input.required<SigningElementPackageViewModelComplete>();
	public isEnvelopeComplete_ = computed(
		() => this.envelope().status === STATUSES.COMPLETE,
	);

	public packageName_ = computed(() =>
		this.envelopeService.getOriginalPackageName(this.envelope()?.id),
	);

	public matterId = input.required<string>();

	public routeToPackage = computed(() =>
		UrlService.Matter_Documents_Packages_Package(
			this.matterId(),
			this.envelope().id,
		),
	);

	public mappedEnvelopeStatusText = computed(() => {
		return this.envelope().status === STATUSES.COMPLETE ? 'Completed' : 'Sent';
	});

	public async onClickViewEnvelope() {
		const completed = this.envelope().status === STATUSES.COMPLETE;

		if (completed) {
			try {
				const documentData = await firstValueFrom(
					this.signItApiService.getCompletedEnvelopeDocument(
						this.matterId(),
						this.envelope().id,
					),
				);

				this.drawerService.openDrawer(PdfModalComponent, {
					title: documentData.fileName || 'Completed Envelope',
					noStyle: true,
					data: {
						base64Encoded: {
							data: documentData.base64Pdf,
							encoding: 'application/pdf',
						},
						fileName: documentData.fileName,
						fileType: 'application/pdf',
					},
				});
			} catch {
				Logger.error('Failed to load completed document');
			}
		} else {
			this.envelopeService.openEnvelopePreviewDrawer(
				this.envelope(),
				'Sent Envelope',
			);
		}
	}
}
